import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ChipProps } from "@mui/material/Chip";

import type {
  ContractTermsResponse,
  OrgResponse,
} from "@bw/shared/types/generated";
import { formatDate } from "@bw/shared/utils";

import { describeAcceptedTerms, formatOptionalDate } from "./terms";

export function SectionTitle({
  title,
  detail,
}: {
  title: string;
  detail?: string;
}) {
  return (
    <Box>
      <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {detail && (
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.25 }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
}

function ReadinessRow({
  label,
  status,
  detail,
  tone = "default",
  action,
}: {
  label: string;
  status: string;
  detail: string;
  tone?: NonNullable<ChipProps["color"]>;
  action?: ReactNode;
}) {
  return (
    <Box
      component="div"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "10rem 9rem minmax(0, 1fr)",
        },
        gap: 1.25,
        alignItems: { md: "center" },
        py: 1.25,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Box component="dd" sx={{ m: 0 }}>
        <Chip size="small" color={tone} variant="outlined" label={status} />
      </Box>
      <Typography
        component="dd"
        color="text.secondary"
        variant="body2"
        sx={{ m: 0, overflowWrap: "anywhere" }}
      >
        {detail}
        {action ? <Box component="span" sx={{ display: "block", mt: 0.5 }}>{action}</Box> : null}
      </Typography>
    </Box>
  );
}

export function LaunchReadiness({
  org,
  owner,
  terms,
  blockers,
}: {
  org: OrgResponse;
  owner: string;
  terms: ContractTermsResponse | null;
  blockers: string[];
}) {
  const termsReady =
    Boolean(terms?.accepted_at) && !Boolean(terms?.requires_reacceptance);
  const termsStatus = !terms ? "No terms published" : termsReady ? "Ready" : "Waiting for owner";
  const termsDetail = terms
    ? termsReady
      ? `${describeAcceptedTerms(terms)}. Effective ${formatOptionalDate(terms.effective_date)}.`
      : `Terms${terms.version_number != null ? ` v${terms.version_number}` : ""} are published; the owner hasn’t accepted them yet.`
    : "This business can’t accept terms until the platform default is published.";
  const termsAction = (
    <Stack direction="row" spacing={2} component="span">
      {!terms ? (
        <Link component={RouterLink} to="/contracts/default-terms">
          Publish platform terms
        </Link>
      ) : null}
      <Link component={RouterLink} to={`/organizations/${org.org_id}/contract-terms`}>
        Custom terms for this business
      </Link>
    </Stack>
  );

  return (
    <Stack component="section" spacing={1.5}>
      <SectionTitle
        title="Launch readiness"
        detail="Backend readiness and blockers determine whether launch approval is available."
      />
      <Box component="dl" sx={{ m: 0 }}>
        <ReadinessRow
          label="Business identity"
          status="Profile submitted"
          detail={`Owner: ${owner}. Submitted ${formatDate(org.created_at)}.`}
        />
        <ReadinessRow
          label="Tax ID"
          status={org.tax_id ? "On file" : "Not provided"}
          detail="Profile information only; backend readiness determines launch eligibility."
        />
        <ReadinessRow
          label="Payments"
          status={
            org.stripe_onboarding_complete
              ? "Onboarding complete"
              : "Onboarding incomplete"
          }
          detail={`Stripe Connect: ${org.stripe_connect_id ? "Connected" : "Not connected"}.`}
          tone={org.stripe_onboarding_complete ? "success" : "warning"}
        />
        <ReadinessRow
          label="Commercial terms"
          status={termsStatus}
          detail={termsDetail}
          tone={termsReady ? "success" : "warning"}
          action={termsAction}
        />
        <ReadinessRow
          label="Launch blockers"
          status={blockers.length === 0 ? "Clear" : `${blockers.length} open`}
          detail={
            org.can_approve
              ? "The backend allows approval."
              : "The backend has not enabled approval."
          }
          tone={org.can_approve ? "success" : "warning"}
        />
      </Box>
    </Stack>
  );
}
